import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Table, Button, Modal, Form, Input, Select, DatePicker, InputNumber,
  Tag, Popconfirm, message, Space, Statistic, Row, Col, Card, Divider,
} from "antd";
import { ArrowLeft, Plus, ClipboardList } from "lucide-react";
import dayjs from "dayjs";
import {
  useQuoteList, useQuoteStats, useQuoteCreate, useQuoteUpdate,
  useQuoteDelete, useQuoteSend, useFinClients,
} from "../../../api-hooks/finance";

const STATUS_TAG: Record<string, string> = {
  draft: "default", sent: "blue", viewed: "cyan", accepted: "green",
  rejected: "red", expired: "orange", converted: "purple",
};
const STATUS_OPTS = [
  { value: "draft", label: "Draft" }, { value: "sent", label: "Sent" },
  { value: "viewed", label: "Viewed" }, { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" }, { value: "expired", label: "Expired" },
  { value: "converted", label: "Converted" },
];

export default function QuotationsPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const cid = Number(companyId);
  const navigate = useNavigate();
  const [msgApi, ctx] = message.useMessage();

  const [search, setSearch] = useState("");
  const [statusF, setStatusF] = useState("");
  const [page, setPage] = useState(1);
  const params = { search: search || undefined, status: statusF || undefined, page, limit: 20 };

  const { data: res, isLoading } = useQuoteList(cid, params);
  const { data: stats } = useQuoteStats(cid);
  const { data: clients = [] } = useFinClients(cid);

  const quotes = res?.data ?? [];
  const total = res?.total ?? 0;

  const [modal, setModal] = useState<{ open: boolean; rec: any | null }>({ open: false, rec: null });
  const [form] = Form.useForm();
  const [lineItems, setLineItems] = useState<any[]>([{ description: "", quantity: 1, unit_price: 0, vat_rate: 20 }]);

  const cb = (msg: string) => ({ onSuccess: () => { msgApi.success(msg); closeModal(); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const create = useQuoteCreate(cid, cb("Quote created."));
  const update = useQuoteUpdate(cid, cb("Quote updated."));
  const remove = useQuoteDelete(cid, cb("Deleted."));
  const send   = useQuoteSend(cid, { onSuccess: () => { msgApi.success("Marked as sent."); }, onError: (e: any) => msgApi.error(e?.message || "Error") });

  function openCreate() {
    setModal({ open: true, rec: null });
    setLineItems([{ description: "", quantity: 1, unit_price: 0, vat_rate: 20 }]);
    form.resetFields();
    form.setFieldsValue({ status: "draft", issue_date: dayjs(), expiry_date: dayjs().add(30, "day"), vat_rate: 20 });
  }

  function openEdit(r: any) {
    setModal({ open: true, rec: r });
    form.setFieldsValue({
      title: r.title, client_id: r.client_id, status: r.status,
      issue_date: r.issue_date ? dayjs(r.issue_date) : null,
      expiry_date: r.expiry_date ? dayjs(r.expiry_date) : null,
      vat_rate: r.vat_rate, discount_amount: r.discount_amount,
      notes: r.notes, terms: r.terms,
    });
    setLineItems(r.line_items?.length ? r.line_items.map((li: any) => ({ description: li.description, quantity: li.quantity, unit_price: li.unit_price, vat_rate: li.vat_rate })) : [{ description: "", quantity: 1, unit_price: 0, vat_rate: 20 }]);
  }

  function closeModal() { setModal({ open: false, rec: null }); form.resetFields(); }

  function handleSubmit(vals: any) {
    const payload = { ...vals, issue_date: vals.issue_date?.format("YYYY-MM-DD"), expiry_date: vals.expiry_date?.format("YYYY-MM-DD"), line_items: lineItems };
    if (modal.rec) update.mutate({ id: modal.rec.id, ...payload });
    else create.mutate(payload);
  }

  const columns = [
    { title: "Quote #", dataIndex: "quote_number", key: "n", render: (v: string, r: any) => <div><div className="font-medium text-sm">{v}</div><div className="text-xs text-slate-400 truncate max-w-[140px]">{r.title}</div></div> },
    { title: "Client", dataIndex: "client_name", key: "c", render: (v: string) => v || "—" },
    { title: "Status", dataIndex: "status", key: "s", render: (v: string) => <Tag color={STATUS_TAG[v]}>{v?.toUpperCase()}</Tag> },
    { title: "Issue", dataIndex: "issue_date", key: "i", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Expiry", dataIndex: "expiry_date", key: "e", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Total", dataIndex: "total", key: "t", render: (v: number) => `£${Number(v || 0).toFixed(2)}` },
    {
      title: "", key: "act", render: (_: any, r: any) => (
        <Space size="small">
          <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
          <Popconfirm title="Mark as sent?" onConfirm={() => send.mutate({ id: r.id })}><Button size="small">Send</Button></Popconfirm>
          <Popconfirm title="Delete?" onConfirm={() => remove.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
        </Space>
      ),
    },
  ];

  const statsCards = stats ? [
    { title: "Draft", value: stats.draft ?? 0, color: "#6b7280" },
    { title: "Sent", value: stats.sent ?? 0, color: "#3b82f6" },
    { title: "Accepted", value: stats.accepted ?? 0, color: "#10b981" },
    { title: "Rejected", value: stats.rejected ?? 0, color: "#ef4444" },
  ] : [];

  return (
    <div>
      {ctx}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-violet-600 hover:text-violet-800 flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Back</button>
          <div>
            <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2"><ClipboardList size={20} className="text-violet-600" /> Quotations — Company #{cid}</h1>
            <p className="text-xs text-slate-500">{total} quotes</p>
          </div>
        </div>
        <Button type="primary" icon={<Plus size={16} />} onClick={openCreate} style={{ background: "#4c1d95", borderColor: "#4c1d95" }}>New Quote</Button>
      </div>

      {stats && (
        <Row gutter={12} className="mb-4">
          {statsCards.map(s => (
            <Col key={s.title} span={6}>
              <Card size="small" className="text-center"><Statistic title={s.title} value={s.value} valueStyle={{ color: s.color, fontSize: 20 }} /></Card>
            </Col>
          ))}
        </Row>
      )}

      <div className="flex gap-3 mb-4">
        <Input.Search placeholder="Search quotes…" value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs" allowClear />
        <Select value={statusF || undefined} onChange={v => setStatusF(v ?? "")} placeholder="All Statuses" options={[{ value: "", label: "All" }, ...STATUS_OPTS]} className="w-44" allowClear />
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
        <Table rowKey="id" dataSource={quotes} columns={columns} loading={isLoading} size="small"
          pagination={{ current: page, pageSize: 20, total, onChange: setPage, showTotal: t => `${t} quotes` }} />
      </div>

      <Modal open={modal.open} onCancel={closeModal} footer={null} destroyOnClose width={640}
        title={modal.rec ? `Edit ${modal.rec.quote_number}` : "New Quote"}>
        <Form form={form} layout="vertical" size="middle" onFinish={handleSubmit} className="pt-2">
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="title" label="Title" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="client_id" label="Client"><Select showSearch allowClear options={clients.map((c: any) => ({ value: c.id, label: `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email }))} /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="issue_date" label="Issue Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="expiry_date" label="Expiry Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <Divider plain className="my-2">Line Items</Divider>
          {lineItems.map((li, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-1 mb-2 items-start">
              <div className="col-span-5"><Input placeholder="Description" value={li.description} onChange={e => { const n = [...lineItems]; n[idx].description = e.target.value; setLineItems(n); }} /></div>
              <div className="col-span-2"><InputNumber className="w-full" placeholder="Qty" min={0} value={li.quantity} onChange={v => { const n = [...lineItems]; n[idx].quantity = v; setLineItems(n); }} /></div>
              <div className="col-span-2"><InputNumber className="w-full" placeholder="£" min={0} value={li.unit_price} onChange={v => { const n = [...lineItems]; n[idx].unit_price = v; setLineItems(n); }} /></div>
              <div className="col-span-2"><InputNumber className="w-full" placeholder="VAT%" min={0} max={100} value={li.vat_rate} onChange={v => { const n = [...lineItems]; n[idx].vat_rate = v; setLineItems(n); }} /></div>
              <div className="col-span-1"><Button size="small" danger onClick={() => setLineItems(lineItems.filter((_, i) => i !== idx))}>×</Button></div>
            </div>
          ))}
          <Button size="small" onClick={() => setLineItems([...lineItems, { description: "", quantity: 1, unit_price: 0, vat_rate: 20 }])} className="mb-4">+ Line Item</Button>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="vat_rate" label="VAT %"><InputNumber className="w-full" min={0} max={100} /></Form.Item>
            <Form.Item name="discount_amount" label="Discount (£)"><InputNumber className="w-full" min={0} /></Form.Item>
          </div>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="terms" label="Terms"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex gap-2 pt-2">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95", borderColor: "#4c1d95" }} loading={create.isPending || update.isPending}>
              {modal.rec ? "Save" : "Create"}
            </Button>
            <Button onClick={closeModal}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
